import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RotateCcw, X, ExternalLink, Send, Eye, Loader2, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RefundModal({ booking, linkedInvoice, cancelledTickets, onClose, onSuccess }) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  // Ticket-level refund state (partially_cancelled, no linked invoice)
  const isTicketLevel = !linkedInvoice && booking?.status === "partially_cancelled" && cancelledTickets?.length > 0;
  const [selectedTicketIdx, setSelectedTicketIdx] = useState(0);
  const [ticketAmount,      setTicketAmount]       = useState("");

  // Legacy refund form state
  const [refundType,    setRefundType]    = useState("full");
  const [partialAmount, setPartialAmount] = useState("");
  const [saving,        setSaving]        = useState(false);

  // Post-creation email state
  const [createdRefund,  setCreatedRefund]  = useState(null);
  const [emailPreview,   setEmailPreview]   = useState(false);
  const [sendingEmail,   setSendingEmail]   = useState(null); // null | "test" | "customer"

  const maxAmount = Math.abs(booking?.selling_price || 0);

  // ── Invoice-linked: redirect to invoice cancellation panel ────────────────
  if (linkedInvoice) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-red-500" /> Process Refund
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-1 text-sm">
            <p className="font-semibold text-blue-800">This booking is linked to a formal invoice.</p>
            <p className="text-blue-700 font-mono text-xs">{linkedInvoice.invoice_number}</p>
          </div>

          <p className="text-sm text-slate-600">
            Refunds for invoice-linked bookings must be processed through the invoice cancellation
            panel. This keeps the financial record in one place and prevents double-counting.
          </p>

          <div className="flex gap-2">
            <Button
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                onClose();
                navigate(`/invoices/${linkedInvoice.id}`);
              }}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Invoice
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Post-creation: email send panel ──────────────────────────────────────
  const handleSendEmail = async (isTest) => {
    setSendingEmail(isTest ? "test" : "customer");
    try {
      const res = await axios.post(
        `${API}/bookings/${createdRefund.id}/send-refund-email`,
        { is_test: isTest },
        { headers: getAuthHeaders() },
      );
      toast.success(res.data.message || (isTest ? "Test email sent to admin" : "Refund email sent"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email send failed");
    } finally {
      setSendingEmail(null);
      setEmailPreview(false);
    }
  };

  if (createdRefund) {
    const refundAmt = Math.abs(createdRefund.selling_price || 0);
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" /> Refund Created
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-500">Ref</span>
              <span className="font-mono font-semibold text-slate-800">{createdRefund.accounting?.invoice_number || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Amount</span>
              <span className="font-mono font-semibold text-emerald-700">€{refundAmt.toFixed(2)}</span>
            </div>
          </div>

          <p className="text-xs text-slate-500">
            No email has been sent. Use the buttons below to send a refund confirmation.
          </p>

          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start text-sm h-9 text-slate-600"
              disabled={!!sendingEmail}
              onClick={() => handleSendEmail(true)}>
              {sendingEmail === "test"
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                : <><Send className="w-4 h-4 mr-2" />Send Test → Admin</>}
            </Button>

            <Button
              className="w-full justify-start text-sm h-9 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!!sendingEmail}
              onClick={() => setEmailPreview(true)}>
              {sendingEmail === "customer"
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                : <><Eye className="w-4 h-4 mr-2" />Preview &amp; Send to Customer</>}
            </Button>

            <Button variant="outline" className="w-full text-sm h-9" onClick={onClose}>
              Done — Send Email Later
            </Button>
          </div>
        </div>

        {/* Confirm customer send dialog */}
        <Dialog open={emailPreview} onOpenChange={v => !v && setEmailPreview(false)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" /> Confirm: Send Refund Email
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2 text-sm">
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Booking</span>
                  <span className="font-mono font-semibold">{booking?.pnr}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Refund amount</span>
                  <span className="font-mono font-semibold">€{refundAmt.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Mode</span>
                  <span className="text-blue-700 font-semibold">Customer</span>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                This will send the refund confirmation to the customer and CC admin.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setEmailPreview(false)}>Cancel</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!!sendingEmail}
                onClick={() => handleSendEmail(false)}>
                {sendingEmail === "customer"
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  : <><Send className="w-4 h-4 mr-2" />Confirm &amp; Send</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Ticket-level refund form (partially_cancelled, no invoice) ──────────
  const handleTicketRefund = async () => {
    const amt = parseFloat(ticketAmount);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive refund amount");
      return;
    }
    const selectedTicket = cancelledTickets[selectedTicketIdx];
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/tickets/${selectedTicket.id}/refund`,
        { refund_amount: amt, notes: `Ticket refund via refund panel` },
        { headers: getAuthHeaders() },
      );
      toast.success("Ticket refund record created");
      onSuccess();
      setCreatedRefund(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setSaving(false);
    }
  };

  if (isTicketLevel && !createdRefund) {
    const selectedTicket = cancelledTickets[selectedTicketIdx];
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-red-500" /> Refund Cancelled Ticket
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {cancelledTickets.length > 1 && (
            <div>
              <Label className="text-xs">Select Cancelled Ticket</Label>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {cancelledTickets.map((t, idx) => (
                  <button key={t.id} onClick={() => setSelectedTicketIdx(idx)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      idx === selectedTicketIdx ? "bg-red-600 border-red-600 text-white" : "border-gray-200 text-gray-700"
                    }`}>
                    {t.pnr || `Ticket ${idx + 1}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="p-3 bg-gray-50 rounded-lg text-sm">
            <p className="text-gray-500 text-xs">Cancelled ticket</p>
            <p className="font-bold text-gray-900">{selectedTicket?.pnr || "—"}</p>
            {selectedTicket?.cancellation_reason && (
              <p className="text-xs text-gray-500 mt-0.5">{selectedTicket.cancellation_reason}</p>
            )}
          </div>

          <div>
            <Label className="text-xs">Refund Amount (€)</Label>
            <Input type="number" step="0.01" min="0.01"
              value={ticketAmount}
              onChange={e => setTicketAmount(e.target.value)}
              placeholder="Enter amount for this ticket only"
              className="mt-1" />
            <p className="text-xs text-slate-400 mt-1">
              Enter only the amount attributable to this cancelled ticket.
            </p>
          </div>

          <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
            This creates a refund record (REF-XXX) for the cancelled ticket only.
            The valid return ticket and its financial records are unaffected.
            No email will be sent automatically.
          </div>

          <div className="flex gap-2">
            <Button onClick={handleTicketRefund} disabled={saving}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white">
              {saving ? "Creating..." : "Confirm Ticket Refund"}
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Legacy refund form (no linked invoice) ────────────────────────────────
  const handleRefund = async () => {
    if (refundType === "partial") {
      const amt = parseFloat(partialAmount);
      if (!amt || amt <= 0 || amt > maxAmount) {
        toast.error(`Enter an amount between €0.01 and €${maxAmount.toFixed(2)}`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/bookings/${booking.id}/refund`,
        { refund_type: refundType, partial_amount: refundType === "partial" ? parseFloat(partialAmount) : null },
        { headers: getAuthHeaders() },
      );
      toast.success(`${refundType === "full" ? "Full" : "Partial"} refund created`);
      onSuccess();
      setCreatedRefund(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-red-500" /> Create Refund
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-3 bg-gray-50 rounded-lg text-sm">
          <p className="text-gray-500">Original booking</p>
          <p className="font-bold text-gray-900">{booking?.pnr}</p>
          <p className="text-gray-700">€{maxAmount.toFixed(2)}</p>
        </div>

        <div>
          <Label className="text-xs">Refund Type</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <button onClick={() => setRefundType("full")}
              className={`py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                refundType === "full" ? "bg-red-600 border-red-600 text-white" : "border-gray-200 text-gray-700"
              }`}>
              Full Refund
            </button>
            <button onClick={() => setRefundType("partial")}
              className={`py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                refundType === "partial" ? "bg-amber-500 border-amber-500 text-white" : "border-gray-200 text-gray-700"
              }`}>
              Partial Refund
            </button>
          </div>
        </div>

        {refundType === "partial" && (
          <div>
            <Label className="text-xs">Refund Amount (€)</Label>
            <Input type="number" step="0.01" max={maxAmount}
              value={partialAmount}
              onChange={e => setPartialAmount(e.target.value)}
              placeholder={`Max €${maxAmount.toFixed(2)}`}
              className="mt-1" />
          </div>
        )}

        <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
          This will create a reversal record (REF-XXX) and update the original booking status.
          No email will be sent automatically.
        </div>

        <div className="flex gap-2">
          <Button onClick={handleRefund} disabled={saving}
            className={`flex-1 text-white ${refundType === "full" ? "bg-red-600 hover:bg-red-700" : "bg-amber-500 hover:bg-amber-600"}`}>
            {saving ? "Creating..." : `Confirm ${refundType === "full" ? "Full" : "Partial"} Refund`}
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
